import { SetStateAction, useCallback, useMemo, useSyncExternalStore } from "react";
import { strFromU8, strToU8, unzlibSync, zlibSync } from "fflate";
import isEqual from "lodash.isequal";
import qs from "qs";
import { z, ZodType } from "zod/v4";

type Encoder = "qs" | "compress";

const events = ["popstate"];
function subscribe(callback: () => void) {
  for (const event of events) {
    window.addEventListener(event, callback);
  }
  return () => {
    for (const event of events) {
      window.removeEventListener(event, callback);
    }
  };
}

export function decodeCompressed(input: unknown): unknown {
  if (typeof input !== "string") {
    return input;
  }
  if (!/^[A-Za-z0-9_-]*={0,2}$/.test(input)) {
    return input; // Not a base64 encoded string, so cannot be compress
  }

  try {
    const padding = input.length % 4;
    const encoded = input
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(input.length + ((4 - padding) % 4), "="); // Add padding to make it a valid base64 string
    const compressed = Buffer.from(encoded, "base64");
    const buffer = unzlibSync(compressed);
    const value = JSON.parse(strFromU8(buffer));
    return value;
  } catch (error) {
    console.warn("Failed to decode compressed data:", error);
    // If there is an error, it could be because the input was not compressed but was recognised as so or it's just wrong
    // Either way, just return it as the next step will validate the shape of the data and if that fails, it will return the default value
    return input;
  }
}

export function encodeCompressed<T>(input: T): string | T | undefined {
  if (input === undefined) {
    return undefined; // If the input is undefined, we return undefined to avoid adding it to the URL
  }

  try {
    const buffer = strToU8(JSON.stringify(input));
    // zlib is not the best compression algorithm for size from the testing I've done, that was brotli
    // but this package was 8kb and gave a 43.8% reduction compared to brotli's ~1mb and 55.8% reduction (using single stringified JSON from real data)
    // However, brotli also required using WASM, meaning async was needed in this file or an hack & some special code for jest.
    const compressed = zlibSync(buffer);
    const encoded = Buffer.from(compressed).toString("base64");
    const urlSafe = encoded.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_"); // Make it URL safe. The padding is optional (=) so it is safe to remove.
    return urlSafe;
  } catch (error) {
    console.error("Failed to compress data:", error);
    return input; // If compression fails, return the original input
  }
}

function getParams<T>(key: string, parseValue: (value: unknown) => T, encoder: Encoder): T {
  const values = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  let value: unknown = values[key];

  if (encoder === "compress") {
    value = decodeCompressed(value);
  }
  const parsed = parseValue(value);
  return parsed;
}

function setParams<T>(key: string, value: T, encoder: Encoder) {
  const values = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  let encodedValue: T | string | undefined = value;

  if (encoder === "compress") {
    encodedValue = encodeCompressed(value);
  }

  const newQsValue = qs.stringify(
    { ...values, [key]: encodedValue },
    { arrayFormat: "brackets", encodeValuesOnly: true },
  );
  window.history.pushState({}, "", `${window.location.pathname}?${newQsValue}`);
  // manually trigger a popstate event to update the state in useSyncExternalStore
  dispatchEvent(new PopStateEvent("popstate", { state: {} }));
}

function safeishStringify(value: unknown): string | undefined | null {
  if (value === null || value === undefined) {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * This hook allows you to manage a piece of state that is synchronized with a query parameter in the URL.
 *
 * There are length limitations, if the value is too long it **WILL** cause production issues (limit is 8,192 bytes for the **whole** URL).
 * If this state you are setting is large, consider settting the encoder to "compress" to drastically reduce the size. You are less likely to hit this locally since the limit is higher.
 * @param key Which query param in the URL to use
 * @param defaultValue Value to use if the query param is not set or cannot be parsed
 * @param parser Zod schema or a function which takes a value (and the default schema) and returns the parsed value. Zod schema is preferred
 * @param options Configuration options
 * @returns [currentValue, setValue] - A tuple of the current value and a function to update the value
 */
// Overload for Zod schema (type inferred from schema)
export function useURLState<T extends ZodType<unknown>>(
  key: string,
  defaultValue: z.output<T>,
  parser: T,
  options?: {
    encoder?: "qs" | "compress";
  },
): [z.output<T>, (newValue: SetStateAction<z.output<T>>) => void];

// Overload for parseValue function (type inferred from defaultValue)
/* @deprecated Prefer using Zod schemas for URL parsing instead */
export function useURLState<T>(
  key: string,
  defaultValue: T,
  parser: (value: unknown, defaultValue: T) => T,
  options?: {
    encoder?: "qs" | "compress";
  },
): [T, (newValue: SetStateAction<T>) => void];
export function useURLState<T>(
  key: string,
  defaultValue: T,
  parser: ZodType<unknown> | ((value: unknown, defaultValue: T) => T),
  options?: {
    encoder?: "qs" | "compress";
  },
): [T, (newValue: SetStateAction<T>) => void] {
  const encoder = options?.encoder || "qs";

  const parseValue = useMemo(
    () => (value: unknown) => {
      if (typeof parser === "function") {
        return parser(value, defaultValue);
      }

      // Below occurs if parser is a Zod schema
      if (value === undefined) {
        return defaultValue;
      }
      const result = parser.safeParse(value);
      if (result.success) {
        return result.data as T;
      } else {
        console.warn(
          `Failed to parse value for query param, reverting to defaults\n`,
          z.prettifyError(result.error),
        );
        return defaultValue;
      }
    },
    [parser, defaultValue],
  );

  const queryValue = useSyncExternalStore(
    subscribe,
    () => safeishStringify(getParams(key, parseValue, encoder)),
    () => safeishStringify(getParams(key, parseValue, encoder)),
  );

  const setURLState = useCallback(
    (newValue: SetStateAction<T>) => {
      const previousValue = getParams(key, parseValue, encoder);
      if (typeof newValue === "function") {
        newValue = (newValue as (prevState: T) => T)(previousValue);
      }
      // only update if the value has changed
      if (!isEqual(newValue, previousValue)) {
        setParams(key, newValue, encoder);
      }
    },
    [key, parseValue, encoder],
  );

  // memoize the value for referential equality
  const value = useMemo<T>(() => {
    // Not doing JSON.parse here because it strips undefined values and some custom
    // parser functions require undefined values to be passed through
    return queryValue ? getParams(key, parseValue, encoder) : defaultValue;
  }, [queryValue, defaultValue, key, parseValue, encoder]);

  return [value, setURLState];
}

/** @deprecated Prefer using z.coerce.number() for URL parsing instead */
export function parseNumberValue(value: unknown) {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return undefined;
}

/** @deprecated Prefer using z.coerce.boolean() for URL parsing instead */
export function parseBooleanValue(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string" && value) {
    return value === "true";
  }
  return undefined;
}
